/**
 * The app's long-text input (PLAN.md §5.10).
 *
 * A Markdown core with a formatting toolbar and a preview tab, rather than a contenteditable
 * WYSIWYG. Markdown is the storage format either way (§12 renders it through one sanitising
 * pipeline), and a toolbar-over-textarea keeps the input honest: what you type is what is
 * stored, paste behaviour is predictable, and there is no hidden HTML state to diverge from it.
 *
 * Pasted HTML is converted to Markdown on the way in, which is the part people actually need
 * from a rich editor — replies pasted out of Outlook keep their lists and links.
 *
 * Drafts autosave to `localStorage` so a closed tab, a crash or an offline moment doesn't lose a
 * half-written reply (§5.10, and the PWA offline story in §5.27 depends on it).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BoldIcon,
  CodeBracketIcon,
  EyeIcon,
  ItalicIcon,
  LinkIcon,
  ListBulletIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';

import { MarkdownBody } from '@/components/common/MarkdownBody';
import { clearDraft, loadDraft, saveDraft } from '@/utils/drafts';
import { htmlToMarkdown } from '@/utils/htmlToMarkdown';
import { clsx } from '@/utils/clsx';

/** Long enough not to thrash localStorage on every keystroke, short enough to survive a crash. */
const AUTOSAVE_MS = 500;

interface ToolbarAction {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Text inserted before and after the selection. */
  wrap: [string, string];
  /** Used instead of the wrap when nothing is selected. */
  placeholder: string;
}

const ACTIONS: ToolbarAction[] = [
  { label: 'Bold', icon: BoldIcon, wrap: ['**', '**'], placeholder: 'bold text' },
  { label: 'Italic', icon: ItalicIcon, wrap: ['*', '*'], placeholder: 'italic text' },
  { label: 'Code', icon: CodeBracketIcon, wrap: ['`', '`'], placeholder: 'code' },
  { label: 'Link', icon: LinkIcon, wrap: ['[', '](https://)'], placeholder: 'link text' },
  { label: 'Bullet list', icon: ListBulletIcon, wrap: ['\n- ', ''], placeholder: 'item' },
];

export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  rows = 4,
  /** Stable key for draft autosave; omit to disable it (e.g. in a create dialog). */
  draftKey,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  draftKey?: string;
  ariaLabel: string;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [preview, setPreview] = useState(false);
  const [restored, setRestored] = useState(false);

  // Restore a draft once, and only into an empty editor — never clobber text the caller
  // supplied (an edit form is pre-populated with the real content).
  useEffect(() => {
    if (!draftKey || restored) return;
    setRestored(true);
    if (value) return;
    const saved = loadDraft(draftKey);
    if (saved) onChange(saved);
  }, [draftKey, restored, value, onChange]);

  useEffect(() => {
    if (!draftKey || !restored) return;
    const timer = window.setTimeout(() => {
      if (value) saveDraft(draftKey, value);
      else clearDraft(draftKey);
    }, AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [value, draftKey, restored]);

  const applyAction = useCallback(
    (action: ToolbarAction) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const { selectionStart, selectionEnd } = textarea;
      const selected = value.slice(selectionStart, selectionEnd) || action.placeholder;
      const [before, after] = action.wrap;
      const next = value.slice(0, selectionStart) + before + selected + after + value.slice(selectionEnd);
      onChange(next);

      // Put the cursor around the inserted text so the user can keep typing over the
      // placeholder — otherwise every toolbar click needs a follow-up click to reposition.
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(selectionStart + before.length, selectionStart + before.length + selected.length);
      });
    },
    [value, onChange],
  );

  const onPaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const html = event.clipboardData.getData('text/html');
      if (!html) return; // Plain text pastes fall through to the browser's own handling.

      const markdown = htmlToMarkdown(html);
      if (!markdown) return;

      event.preventDefault();
      const textarea = event.currentTarget;
      const { selectionStart, selectionEnd } = textarea;
      onChange(value.slice(0, selectionStart) + markdown + value.slice(selectionEnd));
      requestAnimationFrame(() => {
        const caret = selectionStart + markdown.length;
        textarea.setSelectionRange(caret, caret);
      });
    },
    [value, onChange],
  );

  return (
    <div className="rounded-lg border border-surface-300 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500 dark:border-surface-600">
      <div className="flex items-center gap-0.5 border-b border-surface-200 px-2 py-1 dark:border-surface-700">
        {ACTIONS.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={() => applyAction(action)}
            disabled={preview}
            aria-label={action.label}
            title={action.label}
            className="rounded p-1.5 text-surface-500 transition-colors hover:bg-surface-100 disabled:opacity-40 dark:hover:bg-surface-800"
          >
            <action.icon className="h-4 w-4" aria-hidden="true" />
          </button>
        ))}

        <button
          type="button"
          onClick={() => setPreview((current) => !current)}
          aria-pressed={preview}
          className={clsx(
            'ml-auto flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors',
            preview
              ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300'
              : 'text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-800',
          )}
        >
          {preview ? <PencilIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
          {preview ? 'Write' : 'Preview'}
        </button>
      </div>

      {preview ? (
        <div className="min-h-[6rem] px-3 py-2">
          {value ? (
            <MarkdownBody source={value} />
          ) : (
            <p className="text-sm text-surface-400">Nothing to preview.</p>
          )}
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onPaste={onPaste}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className="w-full resize-y border-0 bg-transparent px-3 py-2 text-sm text-surface-900 placeholder:text-surface-400 focus:outline-none focus:ring-0 dark:text-surface-100"
        />
      )}
    </div>
  );
}
